import { CloseOutlined, SearchOutlined } from '@mui/icons-material';
import { IconButton, InputAdornment, Typography } from '@mui/material';
import { Stack } from '@mui/material';
import { Box } from '@mui/material';
import { OutlinedInput } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useAsyncDebounce } from 'react-table';
import KeyboardArrowUpSharpIcon from '@mui/icons-material/KeyboardArrowUpSharp';
import KeyboardArrowDownSharpIcon from '@mui/icons-material/KeyboardArrowDownSharp';
import { Divider } from '@mui/material';
import { CircularProgress } from '@mui/material';

interface DynamicSearchWithArrowProps {
  placeholder?: string;
  fullWidth?: boolean;
  fetchData?: (_value: string) => Promise<any[]>;
  onSelectData?: (_a?: any) => void;
  fileNavigation?: boolean;
  setFileNavigation?: any;
}

const DynamicSearchWithArrow = ({
  placeholder = 'Search',
  fullWidth,
  fetchData,
  onSelectData,
  fileNavigation,
  setFileNavigation
}: DynamicSearchWithArrowProps) => {
  const [currentDataView, setCurrentDataView] = useState<number>(0);
  const [searchData, setSearchData] = useState<any[]>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [value, setValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const onChange = useAsyncDebounce((value) => {
    if (value) {
      fetchSearch(value);
    } else {
      setSearchData([]);
      setCurrentDataView(0);
      if (onSelectData) onSelectData();
    }
  }, 400);

  const handleClear = () => {
    setValue('');
    onChange('');
    setIsFocused(false);
    setSearchData([]);
    setCurrentDataView(0);
    if (onSelectData) {
      onSelectData();
    }
  };

  useEffect(() => {
    if (fileNavigation) {
      handleClear();
      setFileNavigation(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileNavigation]);

  const handleFocus = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const fetchSearch = async (searchValue: string) => {
    try {
      if (!fetchData) {
        return;
      }
      setIsFetching(true);
      const data = await fetchData(searchValue);
      if (data.length > 0) {
        setSearchData(data);
        if (onSelectData) {
          onSelectData(data[0]);
        }
      } else {
        setSearchData([]);
        if (onSelectData) {
          onSelectData();
        }
      }
      setIsFetching(false);
      setCurrentDataView(data.length > 0 ? 1 : 0);
    } catch (e) {
      setIsFetching(false);
      console.error(e);
    }
  };

  const handleNext = () => {
    if (onSelectData) {
      onSelectData(searchData[currentDataView]);
      setCurrentDataView(currentDataView + 1);
    }
  };

  const handlePrev = () => {
    if (onSelectData) {
      onSelectData(searchData[currentDataView - 2]);
      setCurrentDataView(currentDataView - 1);
    }
  };

  return (
    <Box className="dynamic-search-with-arrow-container">
      <OutlinedInput
        inputRef={inputRef}
        value={value || ''}
        onChange={(e) => {
          setValue(e.target.value);
          onChange(e.target.value);
        }}
        placeholder={placeholder}
        id="start-table-search"
        className="GlobalSearchFilter input-container"
        startAdornment={
          <InputAdornment position="start">
            <IconButton className="search-button" onClick={handleFocus} edge="start" size="medium">
              <SearchOutlined className="search-icon" style={{ fontSize: '1.25rem' }} />
            </IconButton>
          </InputAdornment>
        }
        endAdornment={
          <InputAdornment position="end">
            <Stack direction="row" alignItems="center" gap="4px">
              {isFetching ? (
                <CircularProgress className="fetching-spinner" size={20} />
              ) : (
                <Typography className="count-text">
                  {currentDataView}/{searchData.length}
                </Typography>
              )}

              <Divider orientation="vertical" flexItem />
              {value && (
                <IconButton className="close-button" onClick={handleClear} edge="end" size="small">
                  <CloseOutlined className="close-icon" fontSize="small" />
                </IconButton>
              )}
              <IconButton
                className="nav-button"
                onClick={handleNext}
                edge="end"
                size="small"
                disabled={searchData.length === 0 || currentDataView >= searchData.length}
              >
                <KeyboardArrowDownSharpIcon className="close-icon" fontSize="small" />
              </IconButton>
              <IconButton
                className="nav-button"
                onClick={handlePrev}
                edge="end"
                size="small"
                disabled={searchData.length === 0 || currentDataView <= 1}
              >
                <KeyboardArrowUpSharpIcon className="close-icon" fontSize="small" />
              </IconButton>
            </Stack>
          </InputAdornment>
        }
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
        }}
        sx={{
          width: fullWidth ? '100%' : isFocused || value ? 300 : 40,
          transition: 'width 0.3s',
          '& .MuiOutlinedInput-input': {
            width: '100%',
            '&:focus': {
              width: 'calc(100% - 24px)'
            }
          },
          '& .MuiInputAdornment-positionStart': {
            marginRight: '8px'
          }
        }}
      />
    </Box>
  );
};

export default DynamicSearchWithArrow;
